/**
 * PQL ⇄ FilterGroup interop — the bridge between the AST and the item list's
 * two-level builder shape (groups ANDed; rules within a group AND/OR).
 *
 * filterGroupsToAst is total: every builder state has an AST.
 * astToFilterGroups is partial: NOT nodes and >2-level nesting have no builder
 * representation and return null — those queries live in statement mode only.
 */

import { isCombinator, type FilterRule, type QueryNode } from './types';
import type { FilterGroup } from '../../components/prioritization/item-list/hooks/useItemListState';

export type { FilterGroup };

let groupSeq = 0;
function nextGroupId(): string {
  return `pql-group-${++groupSeq}`;
}

export function filterGroupsToAst(groups: FilterGroup[]): QueryNode | null {
  const parts: QueryNode[] = groups
    .filter((g) => g.rules.length > 0)
    .map((g) =>
      g.rules.length === 1
        ? g.rules[0]
        : { op: g.conjunction === 'OR' ? ('or' as const) : ('and' as const), children: [...g.rules] },
    );
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0];
  // Flatten AND-conjunction groups into the top-level AND (canonical form).
  const children: QueryNode[] = [];
  for (const p of parts) {
    if (isCombinator(p) && p.op === 'and') children.push(...p.children);
    else children.push(p);
  }
  return { op: 'and', children };
}

function leavesOf(node: QueryNode, op: 'and' | 'or'): FilterRule[] | null {
  if (!isCombinator(node)) return [node];
  if (node.op !== op) return null;
  const rules: FilterRule[] = [];
  for (const c of node.children) {
    if (isCombinator(c)) return null;
    rules.push(c);
  }
  return rules;
}

/**
 * Convert an AST back to builder groups, or null when the query uses features
 * the builder can't represent (negation, >2-level nesting).
 */
export function astToFilterGroups(node: QueryNode | null): FilterGroup[] | null {
  if (node == null) return [];

  if (!isCombinator(node)) {
    return [{ id: nextGroupId(), conjunction: 'AND', rules: [node] }];
  }

  if (node.op === 'not') return null;

  if (node.op === 'or') {
    const rules = leavesOf(node, 'or');
    return rules ? [{ id: nextGroupId(), conjunction: 'OR', rules }] : null;
  }

  // AND node: consecutive leaf children coalesce into one AND group; each
  // OR-of-leaves child becomes its own OR group.
  const groups: FilterGroup[] = [];
  let pendingLeaves: FilterRule[] = [];
  const flushLeaves = () => {
    if (pendingLeaves.length > 0) {
      groups.push({ id: nextGroupId(), conjunction: 'AND', rules: pendingLeaves });
      pendingLeaves = [];
    }
  };
  for (const child of node.children) {
    if (!isCombinator(child)) {
      pendingLeaves.push(child);
      continue;
    }
    const orRules = child.op === 'or' ? leavesOf(child, 'or') : null;
    if (orRules) {
      flushLeaves();
      groups.push({ id: nextGroupId(), conjunction: 'OR', rules: orRules });
      continue;
    }
    const andRules = child.op === 'and' ? leavesOf(child, 'and') : null;
    if (andRules) {
      flushLeaves();
      groups.push({ id: nextGroupId(), conjunction: 'AND', rules: andRules });
      continue;
    }
    return null;
  }
  flushLeaves();
  return groups;
}

// ============================================================================
// Semantic equality (ignores leaf ids / labels / display strings)
// ============================================================================

function normalizeLeaf(rule: FilterRule): unknown {
  return { field: rule.field, operator: rule.operator, value: rule.value };
}

function normalizeNode(node: QueryNode): unknown {
  if (!isCombinator(node)) return normalizeLeaf(node);
  if (node.op === 'not') return { op: 'not', child: normalizeNode(node.child) };
  return { op: node.op, children: node.children.map(normalizeNode) };
}

/** Structural equality on the semantic parts of two ASTs (id/label/displayValue ignored). */
export function astEquals(a: QueryNode | null, b: QueryNode | null): boolean {
  if (a == null || b == null) return a == null && b == null;
  return JSON.stringify(normalizeNode(a)) === JSON.stringify(normalizeNode(b));
}
